import React, { useState } from "react";
import {
  Fieldset,
  Legend,
  Field,
  Label,
  ErrorMessage,
} from "@/components/fieldset";
import { Input } from "@/components/input";
import { Checkbox } from "@/components/checkbox";
import { Select } from "@/components/select";
import { Button } from "@/components/button";
import { PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import type { ICreateExamConfigRequest } from "@/apis/exam_configs/createExamConfig";
import type { IExamConfigSubject } from "@/apis/exam_configs/getExamConfigList";

interface ExamConfigFormProps {
  initialData?: Partial<ICreateExamConfigRequest>;
  onSubmit: (data: ICreateExamConfigRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  serverError?: string;
  isEditMode?: boolean;
}

const ExamConfigForm: React.FC<ExamConfigFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  serverError,
  isEditMode = false,
}) => {
  // 폼 데이터 상태 관리
  const [formData, setFormData] = useState<ICreateExamConfigRequest>({
    id: initialData?.id || "",
    // initialData의 subjects에 passage_type이 없을 경우 기본값 설정
    subjects: initialData?.subjects?.map((subject) => ({
      ...subject,
      passage_type: subject.passage_type || "none",
      choice_count: subject.choice_count ?? 4,
    })) || [
      {
        subject: "",
        duration_default: 0,
        question_count_default: 0,
        calculator_allowed: false,
        passage_type: "none",
        choice_count: 4,
        domains: [],
      },
    ],
  });

  // 유효성 검사 오류 상태
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ID 변경 핸들러
  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, id: value }));

    // 오류 메시지 제거
    if (errors.id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.id;
        return newErrors;
      });
    }
  };

  // Subject 변경 핸들러
  const handleSubjectChange = (
    index: number,
    field: keyof IExamConfigSubject,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === index ? { ...subject, [field]: value } : subject
      ),
    }));

    // Subject 관련 오류 메시지 제거
    const errorKey = `subjects.${index}.${String(field)}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Subject 추가
  const handleAddSubject = () => {
    setFormData((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        {
          subject: "",
          duration_default: 0,
          question_count_default: 0,
          calculator_allowed: false,
          passage_type: "none",
          choice_count: 4,
          domains: [],
        },
      ],
    }));
  };

  // Subject 제거
  const handleRemoveSubject = (index: number) => {
    if (formData.subjects.length > 1) {
      setFormData((prev) => ({
        ...prev,
        subjects: prev.subjects.filter((_, i) => i !== index),
      }));
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    const validationErrors: Record<string, string> = {};

    if (!formData.id.trim()) {
      validationErrors.id = "Configuration ID is required";
    }

    formData.subjects.forEach((subject, index) => {
      if (!subject.subject.trim()) {
        validationErrors[`subjects.${index}.subject`] =
          "Subject name is required";
      }
      if (subject.duration_default <= 0) {
        validationErrors[`subjects.${index}.duration_default`] =
          "Duration must be greater than 0";
      }
      if (subject.question_count_default <= 0) {
        validationErrors[`subjects.${index}.question_count_default`] =
          "Question count must be greater than 0";
      }
      // passage_type validation 수정: 유효한 값들 중 하나인지 확인
      const validPassageTypes = ["none", "individual", "shared"];
      if (
        !subject.passage_type ||
        !validPassageTypes.includes(subject.passage_type)
      ) {
        validationErrors[`subjects.${index}.passage_type`] =
          "Please select a valid passage type";
      }
    });

    // 유효성 검사 오류가 있으면 제출하지 않음
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" autoComplete="off">
      {/* 서버 오류 표시 */}
      {serverError && (
        <div className="p-4 bg-red-50 text-red-600 rounded-md">
          {serverError}
        </div>
      )}

      {/* 기본 정보 */}
      <Fieldset>
        <Legend>Configuration Information</Legend>

        <div className="mt-4">
          <Field>
            <Label htmlFor="id">Configuration ID*</Label>
            <div className="text-sm text-zinc-500 mt-1 mb-2">
              Enter a unique identifier for this exam configuration (e.g.,
              "SAT", "ACT")
            </div>
            <Input
              id="id"
              name="id"
              type="text"
              placeholder="e.g., SAT"
              value={formData.id}
              onChange={handleIdChange}
              aria-invalid={!!errors.id}
              disabled={isEditMode}
            />
            {errors.id && <ErrorMessage>{errors.id}</ErrorMessage>}
          </Field>
        </div>
      </Fieldset>

      {/* Subjects */}
      <Fieldset>
        <Legend>Subjects</Legend>
        <div className="mt-2 text-sm text-zinc-500">
          Add all subjects that are part of this exam configuration. Specify the
          duration in minutes, default question count, passage type, and whether
          calculators are allowed.
        </div>

        <div className="mt-4 space-y-4">
          {formData.subjects.map((subject, index) => (
            <div
              key={index}
              className="p-4 border border-gray-200 rounded-md bg-gray-50"
            >
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-medium text-gray-900">
                  Subject {index + 1}
                </h4>
                {formData.subjects.length > 1 && (
                  <Button
                    type="button"
                    color="red"
                    onClick={() => handleRemoveSubject(index)}
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Remove
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field>
                  <Label htmlFor={`subject-name-${index}`}>Subject Name*</Label>
                  <Input
                    id={`subject-name-${index}`}
                    type="text"
                    placeholder="e.g., reading_writing"
                    value={subject.subject}
                    onChange={(e) =>
                      handleSubjectChange(index, "subject", e.target.value)
                    }
                    aria-invalid={!!errors[`subjects.${index}.subject`]}
                  />
                  {errors[`subjects.${index}.subject`] && (
                    <ErrorMessage>
                      {errors[`subjects.${index}.subject`]}
                    </ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor={`duration-${index}`}>
                    Duration (minutes)*
                  </Label>
                  <Input
                    id={`duration-${index}`}
                    type="number"
                    min="1"
                    placeholder="e.g., 64"
                    value={subject.duration_default}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        "duration_default",
                        parseInt(e.target.value, 10) || 0
                      )
                    }
                    aria-invalid={
                      !!errors[`subjects.${index}.duration_default`]
                    }
                  />
                  {errors[`subjects.${index}.duration_default`] && (
                    <ErrorMessage>
                      {errors[`subjects.${index}.duration_default`]}
                    </ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor={`question-count-${index}`}>
                    Default Question Count*
                  </Label>
                  <Input
                    id={`question-count-${index}`}
                    type="number"
                    min="1"
                    placeholder="e.g., 44"
                    value={subject.question_count_default}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        "question_count_default",
                        parseInt(e.target.value, 10) || 0
                      )
                    }
                    aria-invalid={
                      !!errors[`subjects.${index}.question_count_default`]
                    }
                  />
                  {errors[`subjects.${index}.question_count_default`] && (
                    <ErrorMessage>
                      {errors[`subjects.${index}.question_count_default`]}
                    </ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor={`passage-type-${index}`}>Passage Type*</Label>
                  <Select
                    id={`passage-type-${index}`}
                    value={subject.passage_type}
                    onChange={(e) =>
                      handleSubjectChange(index, "passage_type", e.target.value)
                    }
                  >
                    <option value="none">None</option>
                    <option value="individual">Individual</option>
                    <option value="shared">Shared</option>
                  </Select>
                  {errors[`subjects.${index}.passage_type`] && (
                    <ErrorMessage>
                      {errors[`subjects.${index}.passage_type`]}
                    </ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor={`choice-count-${index}`}>
                    객관식 유형
                  </Label>
                  <Select
                    id={`choice-count-${index}`}
                    value={String(subject.choice_count ?? 4)}
                    onChange={(e) =>
                      handleSubjectChange(
                        index,
                        "choice_count",
                        parseInt(e.target.value, 10)
                      )
                    }
                  >
                    <option value="4">4지선다</option>
                    <option value="5">5지선다</option>
                  </Select>
                </Field>

                <Field>
                  <div className="flex items-center mt-6">
                    <Checkbox
                      id={`calculator-${index}`}
                      checked={subject.calculator_allowed}
                      onChange={(checked) =>
                        handleSubjectChange(
                          index,
                          "calculator_allowed",
                          checked
                        )
                      }
                    />
                    <Label htmlFor={`calculator-${index}`} className="ml-2">
                      Calculator Allowed
                    </Label>
                  </div>
                </Field>
              </div>
            </div>
          ))}

          <Button type="button" onClick={handleAddSubject}>
            <PlusIcon className="w-4 h-4" />
            Add Subject
          </Button>
        </div>
      </Fieldset>

      {/* 제출 버튼 */}
      <div className="flex justify-end gap-4">
        <Button type="button" plain onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
            ? "Update Configuration"
            : "Create Configuration"}
        </Button>
      </div>
    </form>
  );
};

export default ExamConfigForm;
